import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, Kicker, SectionHeading } from "@/components/site/primitives";
import { CAPABILITIES, FEATURES, IMAGES } from "@/data";

export const MarqueeStrip = () => (
  <section className="border-y border-slate-200 bg-[#F4F6FB] py-6">
    <Marquee speed={45} gradient={false} autoFill>
      {CAPABILITIES.map((c, i) => (
        <div key={c + i} className="mx-8 flex items-center gap-8">
          <span className="font-display text-3xl font-extrabold text-stroke sm:text-4xl">{c}</span>
          <span className="h-2 w-2 rotate-45 bg-volt" />
        </div>
      ))}
    </Marquee>
  </section>
);

export const CapabilityStrip = () => (
  <section id="solutions" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
    <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr]">
      <Reveal>
        <Kicker num="01">Solutions</Kicker>
        <SectionHeading className="mt-6">
          Your warehouse is more than a <span className="text-volt">stock count.</span>
        </SectionHeading>
      </Reveal>
      <Reveal delay={0.1} className="flex items-end">
        <p className="text-lg leading-relaxed text-slate-500">
          Most systems tell you how much you have. Stockaris tells you where it is,
          what's arriving, what needs picking, and whether you'll have the space —
          across every location and every customer.
        </p>
      </Reveal>
    </div>

    <div className="mt-14 grid grid-cols-2 gap-px overflow-hidden rounded-lg border border-slate-200 bg-slate-200 sm:grid-cols-3 lg:grid-cols-5">
      {CAPABILITIES.map((c, i) => (
        <motion.div
          key={c}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.04 }}
          className="group flex items-center justify-between bg-white px-5 py-6 transition-colors hover:bg-slate-50"
        >
          <span className="font-display text-lg font-bold text-slate-900">{c}</span>
          <span className="font-mono-p text-[10px] text-slate-300 transition-colors group-hover:text-volt">
            0{i + 1}
          </span>
        </motion.div>
      ))}
    </div>
  </section>
);

export const FeatureGrid = () => (
  <section id="features" className="mx-auto max-w-[1400px] px-6 py-24 lg:px-10 lg:py-32">
    <Reveal>
      <Kicker num="02">Features</Kicker>
      <SectionHeading className="mt-6 max-w-2xl">
        Everything your warehouse needs.
      </SectionHeading>
    </Reveal>

    <div className="mt-14 grid auto-rows-[minmax(180px,auto)] grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {FEATURES.map((f, i) => (
        <Reveal key={f.title} delay={i * 0.05} className={f.span || ""}>
          <div
            data-testid={`feature-${f.title.toLowerCase()}`}
            className={`group relative flex h-full flex-col justify-between overflow-hidden rounded-lg border p-6 transition-colors ${
              f.accent
                ? "border-volt/40 bg-volt/[0.05] hover:border-volt/70"
                : "border-slate-200 bg-white hover:border-volt/50 hover:shadow-[0_20px_50px_-30px_rgba(47,68,232,0.35)]"
            }`}
          >
            <div className="flex items-start justify-between">
              <f.icon className={`h-7 w-7 ${f.accent ? "text-volt" : "text-slate-500"}`} strokeWidth={1.5} />
              <span className="font-mono-p text-[10px] text-slate-300">0{i + 1}</span>
            </div>
            <div className="mt-8">
              <h3 className="font-display text-2xl font-bold text-slate-900">{f.title}</h3>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-500">{f.desc}</p>
              {f.slug && (
                <Link
                  to={`/features/${f.slug}`}
                  data-testid={`feature-link-${f.slug}`}
                  className="group/link mt-4 inline-flex items-center gap-1.5 font-mono-p text-[11px] uppercase tracking-widest text-volt"
                >
                  Learn more
                  <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover/link:translate-x-1" />
                </Link>
              )}
            </div>
            {f.accent && (
              <div className="mt-6 flex h-14 items-end gap-1">
                {[40, 60, 48, 72, 66, 88, 80].map((h, k) => (
                  <div key={k} className="flex-1 rounded-sm bg-volt/60" style={{ height: `${h}%` }} />
                ))}
              </div>
            )}
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

export const WarehouseViz = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.15, 1]);

  return (
    <section ref={ref} className="relative h-[85vh] overflow-hidden border-y border-slate-200">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMAGES.vizWarehouse} alt="warehouse racks" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-[#0B1240]/78" />
      </motion.div>
      <div className="relative z-10 mx-auto flex h-full max-w-[1400px] flex-col justify-center px-6 lg:px-10">
        <div className="flex items-center gap-3 font-mono-p text-xs uppercase tracking-[0.25em] text-volt">
          <span className="text-white/50">03</span>
          <span className="h-px w-8 bg-volt/70" />
          Visualisation
        </div>
        <h2 className="mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[0.95] text-white sm:text-6xl lg:text-7xl">
          Don't just manage your warehouse. <span className="text-volt">Understand it.</span>
        </h2>
        <p className="mt-6 max-w-xl text-lg text-white/75">
          A live spatial view of every zone, aisle and location — utilisation, hotspots
          and available space, updated as your team works.
        </p>
      </div>
    </section>
  );
};
